// Affirm.js loader — catalog-only (no backend checkout).
//
// Reads the public key from REACT_APP_AFFIRM_PUBLIC_KEY and the environment
// from REACT_APP_AFFIRM_MODE ("sandbox" by default, "live" to go to prod).
// Private keys are NEVER used client-side — if a server-side flow is ever
// required, put AFFIRM_PRIVATE_KEY in backend/.env, not here.
//
// Loader behaviour:
//   - resolves once affirm.js is ready;
//   - rejects on timeout or script error so <AffirmAsLowAs> can hide gracefully;
//   - safe to call multiple times (idempotent).

const PUBLIC_KEY = (process.env.REACT_APP_AFFIRM_PUBLIC_KEY || '').trim();
const MODE = (process.env.REACT_APP_AFFIRM_MODE || 'sandbox').toLowerCase();

export const AFFIRM_MIN_USD = 50;                 // Affirm's documented minimum
export const AFFIRM_PUBLIC_KEY = PUBLIC_KEY;
export const AFFIRM_MODE = MODE;

const SCRIPT_URL = (MODE === 'production' || MODE === 'live')
  ? 'https://cdn1.affirm.com/js/v2/affirm.js'
  : 'https://cdn1-sandbox.affirm.com/js/v2/affirm.js';

const READY_TIMEOUT_MS = 7000;

let readyPromise = null;

/** Load affirm.js. Resolves when `window.affirm.ui` is callable, rejects otherwise. */
export const initAffirm = () => {
  if (readyPromise) return readyPromise;
  if (typeof window === 'undefined') {
    readyPromise = Promise.reject(new Error('no-window'));
    return readyPromise;
  }
  if (!PUBLIC_KEY) {
    readyPromise = Promise.reject(new Error('no-public-key'));
    return readyPromise;
  }

  readyPromise = new Promise((resolve, reject) => {
    // Standard Affirm.js snippet — do not modify (vendored from docs).
    /* eslint-disable */
    (function (l, g, m, e, a, f, b) {
      var d, c = l[m] || {}, h = document.createElement(f), n = document.getElementsByTagName(f)[0],
          k = function (a, b, c) { return function () { a[b]._.push([c, arguments]); }; };
      c[e] = k(c, e, "set"); d = c[e]; c[a] = {}; c[a]._ = []; d._ = [];
      c[a][b] = k(c, a, b); a = 0;
      for (b = "set add save post open empty reset on off trigger ready setProduct".split(" "); a < b.length; a++) d[b[a]] = k(c, e, b[a]);
      a = 0; for (b = "get token url items".split(" "); a < b.length; a++) d[b[a]] = function () {};
      h.async = !0; h.src = g[f];
      h.onerror = function () { reject(new Error('affirm-script-failed')); };
      n.parentNode.insertBefore(h, n); delete g[f]; d(g); l[m] = c;
    })(window, { public_api_key: PUBLIC_KEY, script: SCRIPT_URL }, 'affirm', 'checkout', 'ui', 'script', 'ready');
    /* eslint-enable */

    const start = Date.now();
    const poll = () => {
      if (window.affirm && window.affirm.ui && typeof window.affirm.ui.ready === 'function') {
        resolve(window.affirm);
        return;
      }
      if (Date.now() - start > READY_TIMEOUT_MS) {
        reject(new Error('affirm-timeout'));
        return;
      }
      setTimeout(poll, 150);
    };
    poll();
  });

  return readyPromise;
};

/** Open Affirm's educational / prequalification modal for a given price (USD). */
export const openAffirmPrequal = (priceUsd) => {
  const amountCents = Math.max(0, Math.round(Number(priceUsd || 0) * 100));
  if (!priceUsd || amountCents < AFFIRM_MIN_USD * 100) return false;
  if (typeof window === 'undefined') return false;
  if (!window.affirm || !window.affirm.ui) return false;
  try {
    // The ALS modal is Affirm's recommended prequalification entry point for
    // "educational" placements (catalog browsing without a cart).
    window.affirm.ui.ready(() => {
      window.affirm.ui.openModal({
        amount: amountCents,
        pageType: 'product',
      });
    });
    return true;
  } catch (_) {
    return false;
  }
};

/** Tell affirm.js to re-scan the DOM for new `.affirm-as-low-as` elements. */
export const refreshAffirm = () => {
  if (typeof window === 'undefined') return;
  try { window.affirm && window.affirm.ui && window.affirm.ui.refresh && window.affirm.ui.refresh(); } catch (_) {}
};

export const affirmEnabled = () => Boolean(PUBLIC_KEY);
